import React from 'react';

import { FormattedMessage } from 'react-intl-next';

import {
	type ACTFlagActionType,
	AdControlDropdownMenu,
	DismissalButton,
	ReasonsButton,
} from '@atlassian/ad-control-toolkit';

import {
	UNDO_DISMISSAL_FAILED,
	UNDO_DISMISSAL_STARTED,
	UNDO_DISMISSAL_SUCCEEDED,
} from '../../common/constants';
import { useAppRecommendationsProvider } from '../../controllers';
import { useDismissal } from '../../controllers/use-dismissal';
import { messages } from '../messages';
import { ReasonsModalBody } from '../reasons-modal-body';

import { type AppRecommendationsAdControlProps } from './types';

const AppRecommendationsAdControl: React.FC<AppRecommendationsAdControlProps> = ({
	productId,
	reasons,
	name,
	enableUndo = false,
	...dropDownMenuProps
}) => {
	const { contextResolver, endpoint, onEvents } = useAppRecommendationsProvider();
	const { dismissRecommendation, undoDismissal } = useDismissal({
		endpoint,
		contextResolver,
	});

	const handleOnDismiss = React.useCallback(() => {
		dismissRecommendation(productId);
		// To ensure the user experience is smooth, we remain the current behaviour and don't need to wait for the promise to resolve
		return Promise.resolve();
	}, [dismissRecommendation, productId]);

	const handleOnUndoDismissal = React.useCallback(async () => {
		onEvents?.({
			type: UNDO_DISMISSAL_STARTED,
			itemId: productId,
		});

		try {
			await undoDismissal(productId);
			onEvents?.({
				type: UNDO_DISMISSAL_SUCCEEDED,
				itemId: productId,
			});
		} catch (e) {
			onEvents?.({
				type: UNDO_DISMISSAL_FAILED,
				itemId: productId,
			});
		}
	}, [onEvents, productId, undoDismissal]);

	return (
		<AdControlDropdownMenu itemId={productId} {...dropDownMenuProps}>
			{(props) => (
				<>
					<DismissalButton
						{...props}
						onClick={handleOnDismiss}
						flagProps={{
							actions: (
								[
									enableUndo && {
										content: <FormattedMessage {...messages.undo} />,
										onClick: (e: React.MouseEvent<HTMLElement, MouseEvent>, dismissFlag) => {
											handleOnUndoDismissal();

											dismissFlag();
										},
									},
								] as Array<ACTFlagActionType | false>
							).filter((action): action is ACTFlagActionType => !!action),
						}}
					/>
					{reasons !== undefined && reasons !== null && reasons.length !== 0 ? (
						<ReasonsButton
							{...props}
							modalProps={{
								body: <ReasonsModalBody productId={productId} name={name} reasons={reasons} />,
							}}
						/>
					) : null}
				</>
			)}
		</AdControlDropdownMenu>
	);
};

export default AppRecommendationsAdControl;
